import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { WarningIcon as Warning, ShieldWarningIcon as ShieldWarning, TrashIcon as Trash, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const CONFIRM_PHRASE = "DELETE ALL DATA";

export default function AdminReset() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (user && user.role !== "admin") navigate("/dashboard");
  }, [user, navigate]);

  const loadPreview = () => {
    setLoading(true);
    api.get("/admin/reset/preview")
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load preview"))
      .finally(() => setLoading(false));
  };
  useEffect(loadPreview, []);

  const execute = async () => {
    if (confirm !== CONFIRM_PHRASE) {
      toast.error(`Type "${CONFIRM_PHRASE}" exactly to confirm.`);
      return;
    }
    if (!password) {
      toast.error("Re-enter your admin password.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        "/admin/reset/execute",
        { confirm_phrase: confirm, password },
        { timeout: 5 * 60 * 1000 }, // 5-min ceiling for very large wipes
      );
      setResult(r.data);
      toast.success(`Wiped ${r.data.docs_deleted.toLocaleString()} docs + ${r.data.users_deleted.toLocaleString()} users.`);
      setConfirm("");
      setPassword("");
    } catch (e) {
      // Surface the real error so users aren't stuck on "Reset failed".
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
          : e?.message || "Reset failed (unknown error)";
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error("Reset execute failed:", e?.response?.status, e?.response?.data || e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl">
        <div className="flex items-center gap-3 mb-2">
          <ShieldWarning size={28} weight="duotone" className="text-crimson" />
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson">
            Danger zone · Admin only
          </p>
        </div>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl tracking-tight leading-[1.05]">
          Reset demo data
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-3 leading-relaxed max-w-2xl">
          Permanently wipes every dog, kennel, litter, payment, message, and
          test user — leaving you with a clean registry. Preserved: your admin
          account, allow-listed gmail/own-domain users, the breed catalog,
          pricing, legal documents, seller standards, accounting, and business
          documents.
        </p>
        <p className="text-sm text-crimson font-semibold mt-3">
          This action is irreversible. There is no undo.
        </p>

        {result && (
          <div
            className="mt-8 bg-emerald-50 border border-emerald-200 rounded-xl p-6"
            data-testid="reset-result"
          >
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={24} weight="fill" className="text-emerald-600" />
              <h2 className="font-spectral text-2xl">Reset complete</h2>
            </div>
            <ul className="text-sm space-y-1">
              <li><strong>{result.docs_deleted.toLocaleString()}</strong> documents deleted</li>
              <li><strong>{result.users_deleted.toLocaleString()}</strong> users deleted</li>
              <li><strong>{result.users_preserved.toLocaleString()}</strong> users preserved</li>
              <li><strong>{result.collections_wiped.length}</strong> collections wiped</li>
              <li className="text-[#6B6B6B] pt-1">Completed at {new Date(result.completed_at).toLocaleString()}</li>
            </ul>
            <Button
              onClick={loadPreview}
              variant="outline"
              className="rounded-full mt-4"
              data-testid="reset-refresh-preview"
            >
              Refresh preview
            </Button>
          </div>
        )}

        {loading && <div className="mt-8 text-[#6B6B6B]">Loading preview…</div>}

        {!loading && preview && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-white border border-[#E5E5E5] rounded-xl p-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 mb-3">
                Will be preserved
              </p>
              <div className="text-2xl font-spectral mb-1">
                {preview.preserved_users.length} users
              </div>
              <ul className="text-xs text-[#6B6B6B] space-y-1 mb-4" data-testid="reset-preserved-users">
                {preview.preserved_users.map((u) => (
                  <li key={u.id}>
                    <span className="text-ink">{u.email}</span>
                    {u.role === "admin" && (
                      <span className="ml-1 bg-ink text-white text-[9px] rounded px-1.5 py-0.5 uppercase tracking-wider">admin</span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700 mb-2">
                Admin config kept ({preview.collections_to_keep.length})
              </p>
              <ul className="text-xs text-[#6B6B6B] columns-2 gap-3" data-testid="reset-kept-collections">
                {preview.collections_to_keep.map((c) => (
                  <li key={c.collection}>
                    <span className="text-ink">{c.collection}</span>
                    <span className="text-[#6B6B6B]"> · {c.doc_count}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#FFF5F5] border border-[#FCA5A5]/50 rounded-xl p-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-crimson mb-3">
                Will be deleted
              </p>
              <div className="text-2xl font-spectral mb-1">
                {preview.delete_user_count.toLocaleString()} users
              </div>
              <div className="text-2xl font-spectral mb-3">
                {preview.total_docs_to_delete.toLocaleString()} docs
              </div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-crimson mb-2">
                Collections wiped ({preview.collections_to_wipe.length})
              </p>
              <ul className="text-xs text-[#6B6B6B] columns-2 gap-3 max-h-72 overflow-y-auto" data-testid="reset-wipe-collections">
                {preview.collections_to_wipe.map((c) => (
                  <li key={c.collection}>
                    <span className="text-ink">{c.collection}</span>
                    <span className="text-[#6B6B6B]"> · {c.doc_count}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {!loading && preview && preview.total_docs_to_delete === 0 && preview.delete_user_count === 0 && (
          <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-sm text-emerald-900" data-testid="reset-already-clean">
            Nothing to delete — the database is already clean.
          </div>
        )}

        {!loading && preview && (preview.total_docs_to_delete > 0 || preview.delete_user_count > 0) && (
          <div className="mt-8 bg-white border border-[#E5E5E5] rounded-xl p-6">
            <div className="flex items-start gap-3 mb-5">
              <Warning size={24} weight="duotone" className="text-crimson flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="font-spectral text-xl">Final confirmation</h2>
                <p className="text-sm text-[#6B6B6B] mt-1">
                  Type the phrase below and re-enter your admin password.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="reset-confirm" className="text-xs font-bold uppercase tracking-[0.15em]">
                  Type <code className="text-crimson">{CONFIRM_PHRASE}</code> exactly
                </Label>
                <Input
                  id="reset-confirm"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder={CONFIRM_PHRASE}
                  className="mt-2 font-mono"
                  data-testid="reset-confirm-input"
                />
              </div>
              <div>
                <Label htmlFor="reset-password" className="text-xs font-bold uppercase tracking-[0.15em]">
                  Admin password
                </Label>
                <Input
                  id="reset-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2"
                  data-testid="reset-password-input"
                />
              </div>
              <Button
                onClick={execute}
                disabled={busy || confirm !== CONFIRM_PHRASE || !password}
                className="rounded-full bg-crimson hover:bg-crimson/90 text-white"
                data-testid="reset-execute-btn"
              >
                <Trash size={16} className="mr-1.5" weight="duotone" />
                {busy ? "Wiping…" : `Permanently delete ${preview.total_docs_to_delete.toLocaleString()} docs`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
